/**
 * MyClips V3 — Feature Flags
 *
 * V3 is default OFF. Enabling requires explicit env var or rollout percentage.
 * Deterministic per-uid bucket hashing matches V2's strategy so the same user
 * gets the same variant across sessions.
 *
 * Resolution order:
 *   1. Explicit override via window.__MLV3_MODE__ (for dev console)
 *   2. process.env.REACT_APP_FF_MYCLIPSV3 ("on" | "off" | "rollout")
 *   3. Default: "off"
 *
 * If mode === "rollout", the uid is hashed into [0, 100); if bucket < pct,
 * user sees V3. Otherwise they fall through to the legacy (V1/V2) chain.
 */

const DEFAULT_MODE = "off";
const STORAGE_KEY_DEV = "mlv3.ff.devMode";

export const MYCLIPSV3_MODE = {
  OFF: "off",
  ON: "on",
  ROLLOUT: "rollout",
};

function readEnvMode() {
  try {
    const raw = process.env.REACT_APP_FF_MYCLIPSV3;
    if (typeof raw === "string") {
      const v = raw.toLowerCase();
      if (v === MYCLIPSV3_MODE.ON || v === MYCLIPSV3_MODE.OFF || v === MYCLIPSV3_MODE.ROLLOUT) {
        return v;
      }
    }
  } catch (_) {
    // process.env can throw in non-CRA envs
  }
  return null;
}

function readWindowOverride() {
  if (typeof window === "undefined") return null;
  try {
    const override = (window as any).__MLV3_MODE__;
    if (typeof override === "string") return override.toLowerCase();
    const stored = window.localStorage?.getItem(STORAGE_KEY_DEV);
    if (typeof stored === "string") return stored.toLowerCase();
  } catch (_) {}
  return null;
}

export function getMyClipsV3Mode() {
  return (
    readWindowOverride() ||
    readEnvMode() ||
    DEFAULT_MODE
  );
}

export function getMyClipsV3RolloutPercent() {
  try {
    const raw =
      (typeof window !== "undefined" &&
        window.localStorage?.getItem("mlv3.ff.rolloutPct")) ||
      process.env.REACT_APP_FF_MYCLIPSV3_PCT;
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.max(0, Math.min(100, n));
  } catch (_) {}
  return 0;
}

// Deterministic hash: djb2-style, stable across sessions.
// `uid` gövdede çalışma anında doğrulanıyor (null/undefined/sayı → 100),
// bu yüzden parametre `unknown`: çağıranlar oturum-öncesi null geçebiliyor.
export function rolloutBucketFor(uid: unknown): number {
  if (!uid || typeof uid !== "string") return 100; // unauthenticated → always out
  let h = 5381;
  for (let i = 0; i < uid.length; i++) {
    h = ((h << 5) + h + uid.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 100;
}

export function isMyClipsV3Enabled(uid: unknown): boolean {
  const mode = getMyClipsV3Mode();
  if (mode === MYCLIPSV3_MODE.OFF) return false;
  if (mode === MYCLIPSV3_MODE.ON) return true;
  if (mode === MYCLIPSV3_MODE.ROLLOUT) {
    const pct = getMyClipsV3RolloutPercent();
    if (pct <= 0) return false;
    if (pct >= 100) return true;
    return rolloutBucketFor(uid) < pct;
  }
  return false;
}

// Dev helper — toggle mode from browser console.
// window.mlv3SetMode("on") | "off" | "rollout"
if (typeof window !== "undefined") {
  (window as any).mlv3SetMode = (m: unknown) => {
    try {
      window.localStorage?.setItem(STORAGE_KEY_DEV, String(m));
      (window as any).__MLV3_MODE__ = m;
      // eslint-disable-next-line no-console
      console.info("[mlv3] mode set to", m, "— reload required");
    } catch (_) {}
  };
  (window as any).mlv3GetMode = getMyClipsV3Mode;
}

export default {
  MYCLIPSV3_MODE,
  getMyClipsV3Mode,
  getMyClipsV3RolloutPercent,
  rolloutBucketFor,
  isMyClipsV3Enabled,
};
